import { AxiosResponse } from 'axios';
import { BaseApi } from '@/main/baseApi';
import { GDPR } from '@/modules/kartoteka/gdpr';
import { PARTNER } from '@/modules/kartoteka/constants';

import {
	DependentTableNames, DMS_MODULE, DUPLICITY_CHECK, GDPR_LOG, MERGE, MSG_LOG, NAME, PARTNER_TYPE, CUSTOMER_HISTORY,
	CUSTOMER_FILES, PARTNER_ONGOING_EDITS, VALIDATE_PAN_GSTIN,
} from './constants';

export const gdprTiskUrl = '/Reports/Reports/GetGdprSouhlasReport';

class PartnerProvider extends BaseApi {
	constructor() {
		super(DMS_MODULE, NAME);
		this.tableDataSelectOriginal = this.tableDataSelect;
		this.tableDataSelect = this.tableDataSelectOverride;
	}

	schemaUrl(tableName: DependentTableNames) {
		switch (tableName) {
			case GDPR.name:
				return `/${this.area}/${NAME}/GdprSchema`;
			case GDPR_LOG:
				return `/${this.area}/${NAME}/GdprLogSchema`;
			case MERGE:
				return `/${this.area}/${PARTNER}/MergeValuesSchema`;
			case MSG_LOG:
				return `/${this.area}/${PARTNER}/MsglogSchema`;
			case DUPLICITY_CHECK:
				return `/${this.area}/${PARTNER}/DuplicitySchema`;
			case CUSTOMER_HISTORY:
				return `/${this.area}/${PARTNER}/CustomerHistorySchema`;
			case CUSTOMER_FILES:
				return `/${this.area}/${PARTNER}/CustomerFilesSchema`;
			case PARTNER_ONGOING_EDITS:
				return `/${this.area}/${PARTNER}/UpdateOngoingPartnerData`;
			case VALIDATE_PAN_GSTIN:
				return `/${this.area}/${PARTNER}/ValidateGSTINAndPAN`;
			default:
				throw new Error(`schema for ${tableName} is not implemented yet`);
		}
	}

	tableUrl(tableName: DependentTableNames) {
		switch (tableName) {
			case GDPR.name:
				return `/${this.area}/${NAME}/GdprRows`;
			case GDPR_LOG:
				return `/${this.area}/${NAME}/GdprLogRows`;
			case MERGE:
				return `/${this.area}/${PARTNER}/MergeValuesRows`;
			case MSG_LOG:
				return `/${this.area}/${PARTNER}/MsglogRows`;
			case DUPLICITY_CHECK:
				return `/${this.area}/${PARTNER}/DuplicityRows`;
			case CUSTOMER_HISTORY:
				return `/${this.area}/${PARTNER}/CustomerHistoryRows`;
			case CUSTOMER_FILES:
				return `/${this.area}/${PARTNER}/CustomerFilesRows`;
			case PARTNER_ONGOING_EDITS:
				return `/${this.area}/${PARTNER}/UpdateOngoingPartnerData`;
			case VALIDATE_PAN_GSTIN:
				return `/${this.area}/${PARTNER}/ValidateGSTINAndPAN`;
			default:
				throw new Error(`data for ${tableName} is not implemented yet`);
		}
	}

	tableDataSelectOriginal: (params?: any) => Promise<AxiosResponse<any>>;

	tableDataSelectOverride(params?: any) {
		let typ;
		if (params && params.partnerType) {
			typ = params.partnerType;
		}
		switch (typ) {
			case PARTNER_TYPE.INSURANCE:
				return this.fetch(`/${DMS_MODULE}/${NAME}/SelectInsuranceRows`, params);
			case PARTNER_TYPE.LEASING:
				return this.fetch(`/${DMS_MODULE}/${NAME}/SelectLeasingRows`, params);
			default:
				return this.tableDataSelectOriginal(params);
		}
	}

	obratFilter() {
		return this.fetch(`/${DMS_MODULE}/${NAME}/ObratFilter`);
	}

	obratCelkSchema() {
		return this.fetch(`/${DMS_MODULE}/${NAME}/ObratCelkSchema`);
	}

	obratCelkRows(filter?: object) {
		return this.fetch(`/${DMS_MODULE}/${NAME}/ObratCelkRows`, filter);
	}

	obratMatSchema() {
		return this.fetch(`/${DMS_MODULE}/${NAME}/ObratMatSchema`);
	}

	obratMatRows(filter?: object) {
		return this.fetch(`/${DMS_MODULE}/${NAME}/ObratMatRows`, filter);
	}

	checkCustomerMode(customerId: number, communicationId: number) {
		return this.fetch(`/${DMS_MODULE}/${NAME}/CheckCustomerModeCommunication?customerId=${customerId}&communicationId=${communicationId}`);
	}

	obratSluzSchema() {
		return this.fetch(`/${DMS_MODULE}/${NAME}/ObratSluzSchema`);
	}

	obratSluzRows(filter?: object) {
		return this.fetch(`/${DMS_MODULE}/${NAME}/ObratSluzRows`, filter);
	}

	kontoNr() {
		return this.fetch(`/${DMS_MODULE}/${NAME}/KontoNr`);
	}

	approveDetails(params: object) {
		return this.update(`/${DMS_MODULE}/${NAME}/ApproveDetails`, params);
	}

	rejectDetails(params: object) {
		return this.update(`/${DMS_MODULE}/${NAME}/RejectDetails`, params);
	}

	ChangeToFirma(id: number | string) {
		return this.update(`/${DMS_MODULE}/${NAME}/ChangeToFirma/${id}`);
	}

	ChangeToFO(id: number | string) {
		return this.update(`/${DMS_MODULE}/${NAME}/ChangeToFyzOsoba/${id}`);
	}

	gdprSchema() {
		return this.fetch(`/${DMS_MODULE}/${NAME}/GdprSchema`);
	}

	gdprRows(filter: any) {
		return this.fetch(`/${DMS_MODULE}/${NAME}/GdprRows`, filter);
	}

	customerFilesRows(filter: any) {
		return this.fetch(`/${DMS_MODULE}/${NAME}/CustomerFilesRows`, filter);
	}

	gdprFilter() {
		return this.fetch(`/${DMS_MODULE}/${NAME}/GdprFilter`);
	}

	getVozidloRidicShema() {
		return this.fetch('/Servis/ZakazkaList/VozidloRidicSchema');
	}

	getVozidloRidicRows(params: object) {
		return this.fetch('/Servis/ZakazkaList/VozidloRidicRows', params);
	}

	getPartner(id: number) {
		return this.fetch(`/${DMS_MODULE}/${NAME}/${id}`);
	}

	async zakaznikGetCdb(zakaznik: number | string) {
		return this.post(`/${DMS_MODULE}/${NAME}/GetCdbCustomerRecordStatus`, zakaznik);
	}

	getAresData(params: object) {
		return this.fetch(`/${DMS_MODULE}/${NAME}/BusinesSubjectInfo`, params);
	}

	setGdprSouhlas(filter: any) {
		return this.post(`/${DMS_MODULE}/${NAME}/GdprSouhlas?osdataZdePzn=${filter.osdataZdePzn}&zakaznikId=${filter.zakaznikId}&zpracovosdatId=${filter.zpracovosdatId}`);
	}

	saveDocument(fileList: any, ZakaznikId: null | number) {
		const file = new FormData();
		file.append('file', fileList.raw, fileList.name);
		return this.uploadMultipart(`/${DMS_MODULE}/${NAME}/SaveDocument?ZakaznikId=${ZakaznikId}`, file);
	}

	getDocuments(fileId: number) {
		return this.fetch(`/${DMS_MODULE}/${NAME}/GeCustomerFilestDocument?fileId=${fileId}`);
	}

	deleteDocuments(fileId: number) {
		return this.post(`/${DMS_MODULE}/${NAME}/DeleteDocument?fileId=${fileId}`);
	}

	validateGSTINAndPAN(PAN: null | string, GSTIN: null | string, ZakaznikId: number): any {
		return this.fetch(`/${DMS_MODULE}/${NAME}/ValidateGSTINAndPAN?PAN=${PAN}&GSTIN=${GSTIN}&ZakaznikId=${ZakaznikId}`);
	}

	updatePartnerOngoingEditData(updateData: any) {
		this.update(`/${DMS_MODULE}/${NAME}/UpdateOngoingPartnerData`, updateData);
	}

	async getOemApproval(customerId: number) {
		return this.fetch(`/${DMS_MODULE}/${NAME}/GetOemApproval?customerId=${customerId}`);
	}

	async getStateByCode(stateCode: any) {
		return this.fetch(`/Kartoteka/ZakaznikCDB/GetStateByCode?stateCode=${stateCode}`);
	}

	getVehicleDetailsByVIN(vin: null | string, goldenAccountId: null | string) {
		return this.fetch(`/Kartoteka/Vozidlo/GetVehicleDetailsByVIN?vin=${vin}&goldenAccountId=${goldenAccountId}`);
	}

	getCustomerFromCdb(searchString:null|string, VIN:null|string, licensePlate:null|string){
		return this.fetch(`/${DMS_MODULE}/${NAME}/CDBFuzzyCustomerSearch?searchString=${searchString}&VIN=${VIN}&licensePlate=${licensePlate}`);
	}

	getIndividualCustomerType() {
        return this.fetch(`/DropdownList/GetIndividualCustomerType/`); 
    }

	getCorporateCustomerType() {
        return this.fetch(`/DropdownList/GetCorporateCustomerType/`); 
    }
	getCustomerByGoldenAccountId( customerId: number,vin: string | null,refreshflag: number | null) {
		return this.fetch(`/${DMS_MODULE}/${NAME}/getCustomerByGoldenAccountId?customerId=${customerId}&vin=${vin}&refreshflag=${refreshflag}`);
	}
}

export const apiProvider = new PartnerProvider();
